/**
 * CorreccionesPage — un solo lugar para las correcciones del día a día.
 *
 * Antes cada corrección vivía escondida dentro de la ficha de su reserva: para
 * pasar un saldo a CxC había que buscar la reserva, abrirla, ir a Movimientos y
 * encontrar el botón. Acá se escribe el código, se ve de qué se trata y se
 * elige la operación.
 *
 * No duplica nada: abre los MISMOS diálogos de la ficha, que a su vez llaman a
 * los mismos endpoints. Si mañana cambia una regla, cambia en un solo sitio.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Chip, Alert, Stack,
  CircularProgress, Divider, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaymentsIcon from '@mui/icons-material/Payments';
import SavingsIcon from '@mui/icons-material/Savings';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ArticleIcon from '@mui/icons-material/Article';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import CobroMuelleCxCDialog from './CobroMuelleCxCDialog';
import AplicarSaldoFavorDialog from './AplicarSaldoFavorDialog';
import ReliquidarNetoDialog from './ReliquidarNetoDialog';
import BorrarReservaDialog from './BorrarReservaDialog';
import RegistrarSaldoFavorDialog from './RegistrarSaldoFavorDialog';
import CambiarAgenciaDialog from './CambiarAgenciaDialog';
import ElegirPagoDialog, { movimientoCorregible, dentroDelPlazo, venceCorreccion }
  from './ElegirPagoDialog';
import EditarMovimientoDialog from './EditarMovimientoDialog';
import ReservaSlideOver from './ReservaSlideOver';
import useToast from '../common/useToast';
import { API_BASE, getAuthHeaders, getEmpresaId, formatPrecio, fmtPax, norm } from './shared/format';

/** La fecha de Bogotá desplazada N días, en YYYY-MM-DD. Todas las ventanas se
 *  calculan contra el día del operador, no contra el del servidor. */
const fechaBogota = (offsetDias = 0) => {
  const hoy = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Bogota' }));
  hoy.setDate(hoy.getDate() + offsetDias);
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
       + `-${String(hoy.getDate()).padStart(2, '0')}`;
};

/** Cuántos días atrás llega cada ventana. La de 7 días es la que se trae del
 *  servidor: hoy y ayer se derivan de ella sin pedir nada más. */
const VENTANAS = [
  { id: 'hoy', label: 'Hoy', dias: 0 },
  { id: 'ayer', label: 'Ayer', dias: 1 },
  { id: '7dias', label: '7 días', dias: 6 },
];

/** Quién firma la corrección, para la traza. Mismo origen que la ficha. */
const usuarioActual = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return { id: u.id || u.user_id || null,
             nombre: u.name || u.nombre || u.display_name || u.email || null };
  } catch { return { id: null, nombre: null }; }
};

const rol = () => {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
};

export default function CorreccionesPage() {
  const { toast, ToastContainer } = useToast();
  // El texto filtra la ventana que está a la vista; solo sale al servidor
  // cuando el operador pide explícitamente buscar en todas las reservas.
  const [texto, setTexto] = useState('');
  const [ventana, setVentana] = useState('hoy');
  const [rango, setRango] = useState({ desde: fechaBogota(-30), hasta: fechaBogota(0) });
  const [rangoAbierto, setRangoAbierto] = useState(false);
  // `lista7` son los últimos 7 días: de ahí salen las tres primeras pestañas y
  // sus conteos con una sola petición. `listaAlterna` es lo que devuelve un
  // rango o una búsqueda global, que no sirven para contar las otras ventanas.
  const [lista7, setLista7] = useState([]);
  const [listaAlterna, setListaAlterna] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [buscando, setBuscando] = useState(false);
  // Lo que ya se corrigió, para no corregir dos veces lo mismo.
  const [ultimas, setUltimas] = useState([]);
  const [reserva, setReserva] = useState(null);
  const [movimientos, setMovimientos] = useState(null);
  const [saldoFavor, setSaldoFavor] = useState({ saldo_a_favor: 0, origenes: [] });
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(null);   // qué diálogo está abierto
  const [cuentas, setCuentas] = useState([]);
  // La ficha completa se abre acá mismo, sin salir de la pantalla: es la misma
  // que se ve al tocar una reserva en la lista de Booking.
  const [fichaAbierta, setFichaAbierta] = useState(false);
  // La cuenta de destino se corrige sobre UN pago: primero se elige cuál y
  // después se abre el mismo diálogo que usa el lápiz de la ficha.
  const [movElegido, setMovElegido] = useState(null);

  const esAdmin = ['admin', 'superadmin'].includes(rol());
  // Corregir a qué cuenta entró un pago es trabajo de todo el backoffice, no
  // solo de un admin: es la misma lista que autoriza el endpoint. Los vouchers
  // sí siguen siendo de admin, porque su edición va por otro endpoint.
  const puedeCorregirCuenta = ['admin', 'superadmin', 'coordinador',
                               'administrativo'].includes(rol());

  const cargarReserva = async (codigoReserva) => {
    const cod = String(codigoReserva || '').trim().toUpperCase();
    if (!cod) return;
    setBuscando(true); setError(null); setMovimientos(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones/buscar/${cod}`,
                            { headers: getAuthHeaders() });
      if (r.status === 404) throw new Error(`No existe ninguna reserva con el código ${cod}.`);
      if (!r.ok) throw new Error('No se pudo buscar la reserva');
      const d = await r.json();
      setReserva(d);
      // El desglose financiero y el saldo a favor de su agencia, que es lo que
      // decide qué operaciones tienen sentido.
      fetch(`${API_BASE}/turismo/reservaciones/${d.id}/movimientos`, { headers: getAuthHeaders() })
        .then(x => x.ok ? x.json() : null).then(setMovimientos).catch(() => {});
      if (d.agencia_id) {
        fetch(`${API_BASE}/turismo/correcciones/agencias/${d.agencia_id}/saldo-a-favor`,
              { headers: getAuthHeaders() })
          .then(x => x.ok ? x.json() : null)
          .then(x => setSaldoFavor(x || { saldo_a_favor: 0, origenes: [] }))
          .catch(() => {});
      } else {
        setSaldoFavor({ saldo_a_favor: 0, origenes: [] });
      }
      fetch(`${API_BASE}/turismo/correcciones/cuentas-destino?empresa_id=${d.empresa_id}`,
            { headers: getAuthHeaders() })
        .then(x => x.ok ? x.json() : []).then(setCuentas).catch(() => setCuentas([]));
    } catch (err) {
      setError(err.message);
    }
    setBuscando(false);
  };

  // ── Las ventanas ──────────────────────────────────────────────────────────
  // Una sola petición de siete días alimenta Hoy, Ayer y 7 días. Con los
  // volúmenes reales (49 reservas en una semana) traerlas todas y filtrar en
  // el navegador es más rápido que ir al servidor en cada clic.
  const cargarVentana7 = useCallback(async () => {
    setCargandoLista(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones`
        + `?empresa_id=${getEmpresaId()}&fecha_desde=${fechaBogota(-6)}`
        + `&fecha_hasta=${fechaBogota(0)}&limit=1000`, { headers: getAuthHeaders() });
      const d = await r.json();
      setLista7(d.reservaciones || []);
    } catch { setLista7([]); }
    setCargandoLista(false);
  }, []);

  const cargarRango = async () => {
    setCargandoLista(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones`
        + `?empresa_id=${getEmpresaId()}&fecha_desde=${rango.desde}`
        + `&fecha_hasta=${rango.hasta}&limit=2000`, { headers: getAuthHeaders() });
      const d = await r.json();
      setListaAlterna(d.reservaciones || []);
      setVentana('rango');
    } catch { setError('No se pudo traer ese rango de fechas.'); }
    setCargandoLista(false);
  };

  // El endpoint ignora las fechas cuando llega `q`, así que buscar en toda la
  // base es un paso aparte y explícito: el operador lo pide cuando lo que
  // busca no está en la ventana que tiene a la vista.
  const buscarEnTodas = async () => {
    const q = texto.trim();
    if (q.length < 2) return;
    setCargandoLista(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones`
        + `?empresa_id=${getEmpresaId()}&q=${encodeURIComponent(q)}&limit=200`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setListaAlterna(d.reservaciones || []);
      setVentana('global');
      if (!(d.reservaciones || []).length) {
        setError(`No hay ninguna reserva que coincida con «${q}».`);
      }
    } catch { setError('No se pudo buscar en todas las reservas.'); }
    setCargandoLista(false);
  };

  // ── La franja de últimas correcciones ─────────────────────────────────────
  // Se reconocen por el script de reversa: todas las operaciones del servicio
  // dejan uno en `detalles.respaldo`, y ninguna otra edición lo hace. Así la
  // franja no se llena de ediciones normales de reservas.
  const cargarUltimas = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/turismo/auditoria`
        + `?empresa_id=${getEmpresaId()}&entidad_tipo=reservacion&limit=120`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setUltimas((d.items || []).filter(i => (i.detalles || {}).respaldo).slice(0, 8));
    } catch { setUltimas([]); }
  }, []);

  useEffect(() => { cargarVentana7(); cargarUltimas(); }, [cargarVentana7, cargarUltimas]);

  // ── Lo que se ve ──────────────────────────────────────────────────────────
  const base = ['rango', 'global'].includes(ventana) ? listaAlterna : lista7;
  const diaDe = (r) => String(r.salida_fecha || '').slice(0, 10);

  const porVentana = useMemo(() => {
    if (ventana === 'hoy') return lista7.filter(r => diaDe(r) === fechaBogota(0));
    if (ventana === 'ayer') return lista7.filter(r => diaDe(r) === fechaBogota(-1));
    return base;
  }, [ventana, lista7, listaAlterna]);   // eslint-disable-line react-hooks/exhaustive-deps

  // El texto filtra acá mismo, sobre lo que ya está en memoria.
  const filas = useMemo(() => {
    const q = norm(texto.trim());
    if (q.length < 2) return porVentana;
    return porVentana.filter(r => norm(
      `${r.codigo_reserva} ${r.nombre_cliente} ${r.numero_whatsapp || ''} `
      + `${r.correo || ''} ${r.agencia_nombre || ''}`).includes(q));
  }, [porVentana, texto]);

  const conteos = useMemo(() => ({
    hoy: lista7.filter(r => diaDe(r) === fechaBogota(0)).length,
    ayer: lista7.filter(r => diaDe(r) === fechaBogota(-1)).length,
    '7dias': lista7.length,
  }), [lista7]);

  const tituloVentana = ventana === 'hoy' ? 'Pasadías de hoy'
    : ventana === 'ayer' ? 'Pasadías de ayer'
    : ventana === '7dias' ? 'Últimos 7 días'
    : ventana === 'rango' ? `Del ${rango.desde} al ${rango.hasta}`
    : `Coincidencias con «${texto.trim()}»`;

  // Enter: si el filtro dejó una sola, se abre; si no dejó ninguna, se busca
  // en toda la base. Es lo que uno espera al teclear y darle enter.
  const onSubmitBusqueda = (e) => {
    e.preventDefault();
    if (filas.length === 1) cargarReserva(filas[0].codigo_reserva);
    else if (!filas.length) buscarEnTodas();
  };

  const recargar = () => {
    setAbierto(null);
    if (reserva?.codigo_reserva) cargarReserva(reserva.codigo_reserva);
    cargarUltimas();
    cargarVentana7();
  };

  const saldo = Number(reserva?.saldo_pendiente || 0);
  const embarcada = reserva?.estado_embarque === 'embarcado';
  const f = movimientos?.desglose_financiero || {};
  // Los pagos que de verdad se pueden corregir, con el mismo filtro del lápiz.
  const pagosCorregibles = (movimientos?.movimientos || [])
    .filter(m => movimientoCorregible(m, esAdmin));
  const diaSalidaRes = reserva?.salida?.fecha || reserva?.salida_fecha;
  const enPlazoCorreccion = dentroDelPlazo(diaSalidaRes);
  const fmtVence = venceCorreccion(diaSalidaRes)?.toLocaleDateString('es-CO',
    { day: '2-digit', month: 'short', year: 'numeric' }) || '—';

  // Un excedente solo existe si ya entró plata. La comisión anticipada no
  // cuenta: es un asiento contable, no una transferencia al banco.
  const pagosRecibidos = (movimientos?.movimientos || []).filter(
    m => m.tipo === 'pago_contable'
      && ['muelle', 'pago_directo_cliente', 'pago_agencia'].includes(m.subtipo));
  const totalRecibido = pagosRecibidos.reduce((acc, m) => acc + Number(m.monto || 0), 0);

  // Cada operación dice cuándo tiene sentido y, si no lo tiene, por qué. Un
  // botón deshabilitado que explica enseña más que un botón que no está.
  const operaciones = [
    {
      id: 'cobro',
      label: 'Repartir el saldo pendiente',
      detalle: 'Cuánto se cobra en el muelle y qué pasa con el resto: CxC o cruce con comisión',
      icon: <PaymentsIcon />, color: '#0d9488',
      motivoNo: saldo <= 0 ? 'Esta reserva no tiene saldo pendiente.' : null,
    },
    {
      id: 'aplicar_favor',
      label: 'Aplicar saldo a favor',
      detalle: saldoFavor.saldo_a_favor > 0
        ? `${formatPrecio(saldoFavor.saldo_a_favor, reserva?.moneda)} disponibles de la agencia`
        : 'Usar plata que la agencia entregó de más',
      icon: <SavingsIcon />, color: '#15803d',
      motivoNo: saldo <= 0 ? 'Esta reserva no tiene saldo pendiente.'
        : (saldoFavor.saldo_a_favor <= 0 ? 'La agencia no tiene saldo a favor.' : null),
    },
    {
      id: 'registrar_favor',
      label: 'Registrar saldo a favor',
      detalle: totalRecibido > 0
        ? `Ya entraron ${formatPrecio(totalRecibido, reserva?.moneda)}: si fue de más, el excedente queda a favor de la agencia`
        : 'La agencia transfirió de más y el excedente queda a su favor',
      icon: <VolunteerActivismIcon />, color: '#0891b2',
      motivoNo: !reserva?.agencia_id ? 'Esta reserva no tiene agencia vendedora.'
        : (totalRecibido <= 0
           ? 'Todavía no ha entrado ningún pago: no puede haber excedente.'
           : null),
    },
    {
      id: 'reliquidar',
      label: 'Reliquidar con otro neto',
      detalle: 'La venta se pactó con una tarifa distinta a la vigente',
      icon: <CalculateIcon />, color: '#7c3aed',
      motivoNo: reserva?.numero_factura ? 'Ya tiene factura emitida.' : null,
    },
    {
      id: 'agencia',
      label: 'Cambiar la agencia vendedora',
      detalle: reserva?.agencia_nombre
        ? `Hoy figura ${reserva.agencia_nombre}: la venta la hizo otra agencia`
        : 'La reserva quedó a nombre de la agencia equivocada',
      icon: <StorefrontIcon />, color: '#c2410c',
      // No toca plata: todas las agencias manejan la misma comisión. Por eso
      // solo se niega cuando no hay agencia que cambiar.
      motivoNo: !reserva?.agencia_id
        ? 'Es una venta directa: no tiene agencia vendedora.' : null,
    },
    {
      id: 'cuenta_pago',
      label: 'Corregir la cuenta de un pago',
      detalle: pagosCorregibles.length
        ? `${pagosCorregibles.length} pago(s) con cuenta de destino`
        : 'A qué cuenta entró la plata, el método y la referencia',
      icon: <AccountBalanceIcon />, color: '#1d4ed8',
      // Las mismas dos reglas del lápiz de la ficha: solo pagos con cuenta
      // —los asientos contables no la tienen— y solo dentro del plazo.
      motivoNo: !puedeCorregirCuenta ? 'Tu rol no puede corregir cuentas de pagos.'
        : (!pagosCorregibles.length
           ? 'Esta reserva no tiene pagos con cuenta de destino que corregir.'
           : (!enPlazoCorreccion
              ? `El plazo venció el ${fmtVence}: son 3 días después del pasadía.`
              : null)),
    },
    {
      id: 'borrar',
      label: 'Borrar la reserva',
      detalle: 'Deshace todo su movimiento financiero y libera los cupos',
      icon: <DeleteForeverIcon />, color: '#b91c1c',
      motivoNo: !esAdmin ? 'Solo admin o superadmin.'
        : (embarcada ? 'Ya está embarcada: sus pasajeros viajaron.' : null),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
        Correcciones
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: '#64748b', mb: 2 }}>
        Elige la reserva y qué hay que corregir. Cada operación deja traza de
        auditoría y un respaldo para deshacerla.
      </Typography>

      {/* ── El buscador. Solo mientras no hay una reserva elegida: al elegirla
             se contrae a una franja para volver, y así la pantalla no obliga a
             teclear otro código para la siguiente corrección. ── */}
      {!reserva && (
        <>
          <Paper component="form" onSubmit={onSubmitBusqueda} sx={{ p: 1.5, mb: 1.5 }}>
            <TextField
              fullWidth size="small" value={texto} autoFocus
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Código, nombre, WhatsApp o correo…"
              inputProps={{ autoComplete: 'off', spellCheck: false }}
              InputProps={{
                startAdornment: <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                </InputAdornment>,
                endAdornment: texto ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setTexto('')}>
                      <ClearIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }} />

            <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap' }} useFlexGap>
              {VENTANAS.map(v => (
                <Chip key={v.id} size="small" clickable
                      label={`${v.label} · ${conteos[v.id]}`}
                      onClick={() => { setVentana(v.id); setError(null); }}
                      sx={{
                        fontWeight: 700, fontSize: 11.5,
                        bgcolor: ventana === v.id ? '#0f172a' : '#f1f5f9',
                        color: ventana === v.id ? '#fff' : '#475569',
                        '&:hover': { bgcolor: ventana === v.id ? '#0f172a' : '#e2e8f0' },
                      }} />
              ))}
              <Chip size="small" clickable label={ventana === 'rango' ? tituloVentana : 'Rango de fechas'}
                    onClick={() => setRangoAbierto(o => !o)}
                    sx={{ fontWeight: 700, fontSize: 11.5,
                          bgcolor: ventana === 'rango' ? '#0f172a' : '#f1f5f9',
                          color: ventana === 'rango' ? '#fff' : '#475569' }} />
              {ventana === 'global' && (
                <Chip size="small" label="Búsqueda en toda la base" onDelete={() => setVentana('hoy')}
                      sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: '#e0f2fe', color: '#075985' }} />
              )}
            </Stack>

            {rangoAbierto && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
                     sx={{ mt: 1.25, alignItems: { sm: 'center' } }}>
                <TextField size="small" type="date" label="Desde" value={rango.desde}
                           onChange={(e) => setRango(r => ({ ...r, desde: e.target.value }))}
                           InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="Hasta" value={rango.hasta}
                           onChange={(e) => setRango(r => ({ ...r, hasta: e.target.value }))}
                           InputLabelProps={{ shrink: true }} />
                <Button size="small" variant="contained" onClick={cargarRango}
                        disabled={!rango.desde || !rango.hasta}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Ver ese rango
                </Button>
              </Stack>
            )}
          </Paper>

          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ── Los resultados ── */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                     gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#475569',
                              textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {tituloVentana} · {filas.length}
            </Typography>
            {buscando && <CircularProgress size={13} />}
            {texto.trim().length >= 2 && filas.length !== porVentana.length && (
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                filtradas de {porVentana.length}
              </Typography>
            )}
          </Box>

          {cargandoLista ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : filas.length ? (
            <Paper sx={{ mb: 2, overflow: 'hidden' }}>
              {filas.map((r, i) => (
                <Box key={r.id}
                     onClick={() => cargarReserva(r.codigo_reserva)}
                     sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1,
                           cursor: 'pointer', borderTop: i ? '1px solid #f1f5f9' : 'none',
                           '&:hover': { bgcolor: '#f8fafc' } }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>
                        {r.nombre_cliente}
                      </Typography>
                      {r.estado_embarque === 'embarcado' && (
                        <Chip size="small" label="embarcada"
                              sx={{ height: 17, fontSize: 9.5, fontWeight: 700,
                                    bgcolor: '#dcfce7', color: '#166534' }} />
                      )}
                      {r.estado === 'cancelada' && (
                        <Chip size="small" label="cancelada"
                              sx={{ height: 17, fontSize: 9.5, fontWeight: 700,
                                    bgcolor: '#fee2e2', color: '#991b1b' }} />
                      )}
                      {r.voucher_pendiente_subsanar && (
                        <Chip size="small" label="sin voucher"
                              sx={{ height: 17, fontSize: 9.5, fontWeight: 700,
                                    bgcolor: '#fef3c7', color: '#92400e' }} />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: '#64748b',
                                      overflow: 'hidden', textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap' }}>
                      {r.codigo_reserva} · {String(r.salida_fecha || '').slice(8, 10)}
                      -{String(r.salida_fecha || '').slice(5, 7)} · {fmtPax(r)}
                      {r.agencia_nombre ? ` · ${r.agencia_nombre}` : ' · venta directa'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800,
                                      color: Number(r.saldo_pendiente) > 0 ? '#b45309' : '#94a3b8' }}>
                      {Number(r.saldo_pendiente) > 0
                        ? formatPrecio(r.saldo_pendiente, r.moneda) : 'sin saldo'}
                    </Typography>
                    {Number(r.saldo_pendiente) > 0 && (
                      <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                        {r.saldo_a_cargo_de === 'agencia' ? 'agencia' : 'por cobrar'}
                      </Typography>
                    )}
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18, color: '#cbd5e1', flexShrink: 0 }} />
                </Box>
              ))}
            </Paper>
          ) : (
            <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1 }}>
                {texto.trim().length >= 2
                  ? `Nada con «${texto.trim()}» en ${tituloVentana.toLowerCase()}.`
                  : 'No hay reservas en esta ventana.'}
              </Typography>
              {texto.trim().length >= 2 && ventana !== 'global' && (
                <Button size="small" variant="outlined" onClick={buscarEnTodas}
                        startIcon={<TravelExploreIcon sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Buscarla en todas las reservas
                </Button>
              )}
            </Paper>
          )}
        </>
      )}

      {/* ── Franja para volver a la lista sin teclear otro código ── */}
      {reserva && (
        <Paper sx={{ p: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                  onClick={() => { setReserva(null); setMovimientos(null); setError(null); }}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>
            Volver a {tituloVentana.toLowerCase()} · {filas.length}
          </Button>
          {buscando && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
        </Paper>
      )}

      {reserva && error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {reserva && (
        <>
          {/* De qué se trata la reserva: sin esto uno corrige a ciegas */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between',
                       alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                  {reserva.nombre_cliente}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                  {reserva.codigo_reserva} · {reserva.adultos}A
                  {reserva.ninos ? ` + ${reserva.ninos}N` : ''}
                  {reserva.agencia_nombre ? ` · ${reserva.agencia_nombre}` : ''}
                  {reserva.salida?.fecha ? ` · ${String(reserva.salida.fecha).slice(0, 10)}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={reserva.estado}
                      sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#075985' }} />
                {embarcada && <Chip size="small" label="Embarcada"
                      sx={{ fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }} />}
                {reserva.numero_factura && <Chip size="small" label={`Factura ${reserva.numero_factura}`}
                      sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />}
              </Stack>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'grid', gap: 1.5,
                       gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' } }}>
              <Dato label="Precio total" valor={formatPrecio(reserva.precio_total, reserva.moneda)} />
              <Dato label="Comisión" valor={formatPrecio(f.comision_efectiva ?? reserva.comision_valor, reserva.moneda)} />
              <Dato label="Por cobrar" valor={formatPrecio(saldo, reserva.moneda)}
                    color={saldo > 0 ? '#b45309' : '#16a34a'} />
              <Dato label="A cargo de" valor={saldo > 0 ? (reserva.saldo_a_cargo_de || '—') : 'sin saldo'} />
            </Box>

            <Button size="small" startIcon={<ArticleIcon sx={{ fontSize: 15 }} />}
              onClick={() => setFichaAbierta(true)}
              sx={{ textTransform: 'none', fontSize: 12, mt: 1, px: 0 }}>
              Ver la ficha completa
            </Button>
          </Paper>

          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#475569',
                            textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
            Qué hay que corregir
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.25,
                     gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
            {operaciones.map(op => (
              <Paper key={op.id}
                onClick={() => { if (!op.motivoNo) setAbierto(op.id); }}
                sx={{
                  p: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start',
                  cursor: op.motivoNo ? 'not-allowed' : 'pointer',
                  opacity: op.motivoNo ? 0.55 : 1,
                  borderLeft: `4px solid ${op.motivoNo ? '#cbd5e1' : op.color}`,
                  '&:hover': op.motivoNo ? {} : { bgcolor: '#f8fafc' },
                }}>
                <Box sx={{ color: op.motivoNo ? '#94a3b8' : op.color, mt: 0.25 }}>{op.icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>
                    {op.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                    {op.motivoNo || op.detalle}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {/* Los mismos diálogos de la ficha de la reserva */}
      {reserva && (
        <>
          <CobroMuelleCxCDialog
            open={abierto === 'cobro'} onClose={() => setAbierto(null)}
            reserva={reserva} cuentasDestino={cuentas} onToast={toast} onHecho={recargar} />
          <AplicarSaldoFavorDialog
            open={abierto === 'aplicar_favor'} onClose={() => setAbierto(null)}
            reserva={reserva} disponible={saldoFavor.saldo_a_favor}
            origenes={saldoFavor.origenes} onToast={toast} onHecho={recargar} />
          <RegistrarSaldoFavorDialog
            open={abierto === 'registrar_favor'} onClose={() => setAbierto(null)}
            reserva={reserva} onToast={toast} onHecho={recargar} />
          <ReliquidarNetoDialog
            open={abierto === 'reliquidar'} onClose={() => setAbierto(null)}
            reserva={reserva} resumenFinanciero={f} onToast={toast} onHecho={recargar} />
          <CambiarAgenciaDialog
            open={abierto === 'agencia'} onClose={() => setAbierto(null)}
            reserva={reserva} onToast={toast} onHecho={recargar} />
          <ElegirPagoDialog
            open={abierto === 'cuenta_pago'} onClose={() => setAbierto(null)}
            reserva={reserva} movimientos={movimientos?.movimientos || []}
            permiteVouchers={esAdmin}
            onElegir={(m) => { setAbierto(null); setMovElegido(m); }} />
          <EditarMovimientoDialog
            open={!!movElegido} onClose={() => setMovElegido(null)}
            movimiento={movElegido} cuentasDestino={cuentas}
            usuario={usuarioActual()}
            onSaved={() => { setMovElegido(null); toast.success('Movimiento actualizado'); recargar(); }} />
          <BorrarReservaDialog
            open={abierto === 'borrar'} onClose={() => setAbierto(null)}
            reserva={reserva} onToast={toast}
            onBorrada={() => {
              setAbierto(null); setReserva(null);
              cargarVentana7(); cargarUltimas();
            }} />
        </>
      )}

      {/* La ficha completa: datos, viajeros, movimientos, evidencia y timeline.
          Al cerrarla se recarga, por si se corrigió algo desde adentro. */}
      {fichaAbierta && reserva && (
        <ReservaSlideOver
          reservaId={reserva.id}
          onClose={() => setFichaAbierta(false)}
          onUpdated={() => cargarReserva(reserva.codigo_reserva)}
          zIndex={1400}
        />
      )}

      {/* ── Lo que ya se corrigió. En una pantalla que mueve plata, ver lo
             tocado hoy evita hacer dos veces la misma corrección. ── */}
      {!reserva && ultimas.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <HistoryIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#475569',
                              textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Últimas correcciones
            </Typography>
          </Box>
          <Paper sx={{ overflow: 'hidden' }}>
            {ultimas.map((a, i) => (
              <Box key={a.id}
                   onClick={() => a.entidad_codigo && cargarReserva(a.entidad_codigo)}
                   sx={{ px: 1.25, py: 1, display: 'flex', gap: 1, alignItems: 'flex-start',
                         cursor: a.entidad_codigo ? 'pointer' : 'default',
                         borderTop: i ? '1px solid #f1f5f9' : 'none',
                         '&:hover': a.entidad_codigo ? { bgcolor: '#f8fafc' } : {} }}>
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8', flexShrink: 0,
                                  fontVariantNumeric: 'tabular-nums', mt: 0.15 }}>
                  {new Date(a.created_at).toLocaleString('es-CO', {
                    timeZone: 'America/Bogota', day: '2-digit', month: '2-digit',
                    hour: '2-digit', minute: '2-digit' })}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 11.5, color: '#334155' }}>
                    {a.descripcion}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                    {a.entidad_codigo || '—'}
                    {a.usuario_nombre ? ` · ${a.usuario_nombre}` : ''}
                  </Typography>
                </Box>
                <Tooltip title={`Respaldo: ${(a.detalles || {}).respaldo}`}>
                  <ArticleIcon sx={{ fontSize: 14, color: '#cbd5e1', flexShrink: 0, mt: 0.25 }} />
                </Tooltip>
              </Box>
            ))}
          </Paper>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.5 }}>
            Cada una dejó un script de reversa en el servidor para deshacerla.
          </Typography>
        </Box>
      )}

      <ToastContainer />
    </Box>
  );
}

function Dato({ label, valor, color }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: color || '#0f172a' }}>
        {valor}
      </Typography>
    </Box>
  );
}
